// Inhalt zuweisen (Playlist oder Zeitplan) an Gerät, Gruppe, Standort oder Mandant (PUT /assignments, Upsert).
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { qk, useAssignments, usePlaylists, useSchedules } from '../api/queries';
import type { TargetType } from '../api/types';
import { useToast } from '../components/toast';
import { Dialog, Field } from '../components/ui';

export function AssignDialog({ targets, title, onClose }: { targets: { targetType: TargetType; targetId: string | null }[]; title?: string; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const { data: playlists = [] } = usePlaylists();
  const { data: schedules = [] } = useSchedules();
  const { data: assignments = [] } = useAssignments();
  const existing = targets.length === 1 ? assignments.find((a) => a.targetType === targets[0]!.targetType && a.targetId === targets[0]!.targetId) : undefined;
  const [kind, setKind] = useState<'playlist' | 'schedule'>(existing?.scheduleId ? 'schedule' : 'playlist');
  const [id, setId] = useState(existing?.playlistId ?? existing?.scheduleId ?? '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      for (const tg of targets) {
        await api.put('/assignments', { ...tg, ...(kind === 'playlist' ? { playlistId: id } : { scheduleId: id }) });
      }
      await Promise.all([qc.invalidateQueries({ queryKey: qk.assignments }), qc.invalidateQueries({ queryKey: qk.devices })]);
      toast.show(t('assign.saved'));
      onClose();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!existing) return;
    try {
      await api.del(`/assignments/${existing.id}`);
      await qc.invalidateQueries({ queryKey: qk.assignments });
      onClose();
    } catch (e) {
      toast.error(e);
    }
  };
  const list = kind === 'playlist' ? playlists : schedules;
  return (
    <Dialog title={title ?? t('assign.title')} onClose={onClose} actions={<>
      {existing && <button className="btn ghost danger" onClick={remove}>{t('assign.remove')}</button>}
      <span className="grow" />
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" disabled={!id || busy} onClick={save}>{t('common.save')}</button>
    </>}>
      <div className="muted">{t('assign.what')}</div>
      <div className="seg" role="radiogroup">
        <button role="radio" aria-checked={kind === 'playlist'} className={kind === 'playlist' ? 'on' : ''} onClick={() => { setKind('playlist'); setId(''); }}>{t('nav.playlists')}</button>
        <button role="radio" aria-checked={kind === 'schedule'} className={kind === 'schedule' ? 'on' : ''} onClick={() => { setKind('schedule'); setId(''); }}>{t('nav.schedules')}</button>
      </div>
      <Field label={kind === 'playlist' ? t('assign.playlist') : t('assign.schedule')}>
        <select value={id} onChange={(e) => setId(e.target.value)}>
          <option value="">{t('common.choose')}</option>
          {list.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
      </Field>
      {kind === 'playlist' && playlists.length > 0 && id && !playlists.find((p) => p.id === id)?.latestRevisionNo && <div className="hint warn">{t('assign.unpublished')}</div>}
    </Dialog>
  );
}
